// Row heights of a product page. Excel/LibreOffice never grow a row with a
// custom height, so text that wraps more than the template expects gets cut
// when printed. Heights are therefore computed from the text itself, and the
// font of the page shrinks until the whole table fits on one A4 page.

import {
  COL_PX,
  MAX_STRETCH,
  PAGE_HEIGHT_PT,
  PX_PER_PT,
  ROW_PT,
} from "./quoteLayout";

export const FONT_SIZES = [10, 9, 8, 7];
export const SMALL_FONT_DELTA = 2; // units in labels, website links
const LINE_HEIGHT = 1.2; // x font size
const ROW_PADDING_PT = 4;
const CELL_PADDING_PX = 6;

// Conservative Times New Roman advance widths (em). Overestimating only
// costs a bit of white space; underestimating cuts text on paper.
function charEm(ch) {
  if (ch === " ") return 0.3;
  if (ch !== ch.toLowerCase()) return 0.72; // uppercase letter (incl. Ắ, Đ...)
  if (ch !== ch.toUpperCase() || /[0-9]/.test(ch)) return 0.5;
  if (/[.,:;'!|()[\]/\\-]/.test(ch)) return 0.33;
  return 0.6; // symbols, &, $, ...
}

export function textWidthPx(text, fontPt, bold = false) {
  let em = 0;
  for (const ch of text) em += charEm(ch);
  return em * fontPt * PX_PER_PT * (bold ? 1.08 : 1);
}

// Greedy word wrap, breaking words longer than a line (e.g. URLs)
export function countLines(text, widthPx, fontPt, bold = false) {
  const available = Math.max(10, widthPx - CELL_PADDING_PX);
  let lines = 0;
  String(text)
    .split("\n")
    .forEach((paragraph) => {
      let current = 0;
      let paragraphLines = 1;
      paragraph.split(/(\s+)/).forEach((token) => {
        if (!token) return;
        let w = textWidthPx(token, fontPt, bold);
        if (/^\s+$/.test(token)) {
          current += w;
          return;
        }
        if (current + w <= available) {
          current += w;
          return;
        }
        if (current > 0) {
          paragraphLines++;
          current = 0;
        }
        while (w > available) {
          paragraphLines++;
          w -= available;
        }
        current = w;
      });
      lines += paragraphLines;
    });
  return Math.max(1, lines);
}

const lineHeightPt = (fontPt) => fontPt * LINE_HEIGHT;

function naturalHeights({ t, rows, group, groupIdx, ctxFor }, fontPt) {
  const small = fontPt - SMALL_FONT_DELTA;
  return rows.map((row) => {
    const unit = row.unit && t.units[row.unit] ? " " + t.units[row.unit] : "";
    // the unit is smaller: count it at its own size
    const labelPx =
      textWidthPx(t.rows[row.label], fontPt, true) + textWidthPx(unit, small);
    let lines = Math.ceil(labelPx / (COL_PX[0] - CELL_PADDING_PX));
    const valueFont = row.key === "website" ? small : fontPt;
    group.forEach((product, idx) => {
      if (!product) return;
      const text = String(row.display(ctxFor(product, groupIdx, idx))).trim();
      lines = Math.max(
        lines,
        countLines(
          text,
          COL_PX[idx + 1],
          valueFont,
          !!(row.highlight || row.bold)
        )
      );
    });
    const minPt = lineHeightPt(fontPt) + ROW_PADDING_PT;
    return Math.max(minPt, lines * lineHeightPt(valueFont) + ROW_PADDING_PT);
  });
}

export const DATA_BUDGET_PT =
  PAGE_HEIGHT_PT -
  ROW_PT.header -
  ROW_PT.image -
  ROW_PT.spacer -
  ROW_PT.contact;

/**
 * @returns {{ fontPt, heights: number[], fits: boolean }}
 *   heights of the data rows (points); their sum never exceeds DATA_BUDGET_PT.
 *   fits=false means even the smallest font overflowed and rows were squeezed.
 */
export function layoutProductRows(page) {
  if (!page.rows.length)
    return { fontPt: FONT_SIZES[0], heights: [], fits: true };
  for (const fontPt of FONT_SIZES) {
    const heights = naturalHeights(page, fontPt);
    const total = heights.reduce((a, b) => a + b, 0);
    if (total <= DATA_BUDGET_PT) {
      // fill the page like the preview does, without ballooning short tables
      const stretch = Math.min(MAX_STRETCH, DATA_BUDGET_PT / total);
      return { fontPt, heights: heights.map((h) => h * stretch), fits: true };
    }
  }
  const fontPt = FONT_SIZES[FONT_SIZES.length - 1];
  const heights = naturalHeights(page, fontPt);
  const total = heights.reduce((a, b) => a + b, 0);
  return {
    fontPt,
    heights: heights.map((h) => (h * DATA_BUDGET_PT) / total),
    fits: false,
  };
}
